import React from 'react';

import type { AvailableIconName } from '@/assets/icons';
import { Stack } from '@/components/layout';
import { Skeleton, SvgIcon, Switch, Text } from '@/components/ui';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

type CoachNotificationPreferenceRowProps = {
  icon: AvailableIconName;
  title: string;
  subtitle: string;
  value: boolean;
  onValueChange: (value: boolean) => void;
  disabled?: boolean;
};

export function CoachNotificationPreferenceRow({
  icon,
  title,
  subtitle,
  value,
  onValueChange,
  disabled = false,
}: CoachNotificationPreferenceRowProps) {
  const { scale } = useScaling();
  const styles = useStyles();

  return (
    <Stack direction="row" align="center" gap={scale(10)} style={styles.row}>
      <Stack align="center" justify="center" style={styles.iconTile}>
        <SvgIcon name={icon} size={scale(24)} color={guardianColors['Main/Main']} />
      </Stack>
      <Stack flex={1} minWidth={0}>
        <Text
          variant="Font Guardian App/Subtitle/Subtitle 14 Bold"
          numberOfLines={2}
          style={styles.title}>
          {title}
        </Text>
        <Text
          variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
          numberOfLines={2}
          style={styles.subtitle}>
          {subtitle}
        </Text>
      </Stack>
      <Switch value={value} onValueChange={onValueChange} disabled={disabled} />
    </Stack>
  );
}

/** Same layout as the row above, shown until the saved values arrive so no switch renders in a
 *  state the server never confirmed. */
export function CoachNotificationPreferenceRowSkeleton() {
  const { scale } = useScaling();
  const styles = useStyles();

  return (
    <Stack direction="row" align="center" gap={scale(10)} style={styles.row}>
      <Skeleton width={scale(40)} height={scale(40)} borderRadius={scale(8)} />
      <Stack flex={1} minWidth={0} gap={scale(6)}>
        <Skeleton.Line width="55%" height={scale(14)} />
        <Skeleton.Line width="35%" height={scale(12)} />
      </Stack>
      <Skeleton width={scale(40)} height={scale(24)} borderRadius={scale(12)} />
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  row: {
    paddingLeft: scale(12),
    paddingRight: scale(16),
    paddingVertical: scale(10),
  },
  iconTile: {
    width: scale(40),
    height: scale(40),
    borderRadius: scale(8),
    borderCurve: 'continuous',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: guardianColors['Main/10'],
    borderWidth: scale(1),
    borderColor: colors['1'],
  },
  title: {
    color: guardianColors['Main/Main'],
  },
  subtitle: {
    color: guardianColors['6'],
  },
}));
